"use client";

import { FC, useEffect } from "react";
import {
  Blocks,
  BuilderBlock,
  isEditing,
  isPreviewing,
} from "@builder.io/sdk-react";
import { BuilderBlockProps, BuilderProps } from "@/types";
import { generateItsmImage } from "@/helpers/generateItsmImage";
import { generateItsmPdfAttachment } from "@/helpers/generateItsmPdfAttachment";

type ItsmWrapperBlockProps = {
  tableView: BuilderBlock[];
  confirmationView: BuilderBlock[];
} & BuilderBlockProps &
  BuilderProps;

/**
 * ITSM Wrapper Block
 *
 * Manages the views for ITSM Maturity Model flow:
 * - "table" view: shows the interactive table and registration form
 * - "confirmation" view: shows PDF buttons and generated image
 *
 * States
 * - itsmView: "table" | "confirmation"
 * - itsmImage: base64 image string
 * - itsmFormData: { name, company, challenge }
 *
 * Exposes to window for Builder custom code:
 * - window.generateItsmImage() - captures table as PNG
 * - window.generateItsmPdfAttachment(data) - generates PDF with base64
 */
export const ItsmWrapperBlock: FC<ItsmWrapperBlockProps> = ({
  attributes,
  builderContext,
  builderBlock,
  builderComponents,
  builderLinkComponent,
  tableView,
  confirmationView,
}) => {
  const currentView = (builderContext.rootState?.itsmView as string) ?? "table";
  const builderId = builderBlock?.id;

  // Initialize state on mount and expose helper functions to window
  useEffect(() => {
    // Expose to window for Builder custom code
    window.generateItsmImage = generateItsmImage;
    window.generateItsmPdfAttachment = generateItsmPdfAttachment;

    if (
      builderContext?.rootSetState &&
      builderContext.rootState?.itsmView === undefined
    ) {
      builderContext.rootSetState({
        ...builderContext.rootState,
        itsmView: "table",
      });
    }

    return () => {
      // Only delete if the current value matches what we set
      // This prevents issues if multiple ItsmWrapperBlock instances are mounted
      if (window.generateItsmImage === generateItsmImage) {
        delete window.generateItsmImage;
      }
      if (window.generateItsmPdfAttachment === generateItsmPdfAttachment) {
        delete window.generateItsmPdfAttachment;
      }
    };
  }, [builderContext]);

  const isTableView = currentView === "table";
  const isConfirmationView = currentView === "confirmation";

  const TableViewBlocks = (
    <Blocks
      blocks={tableView}
      parent={builderId}
      path="tableView"
      context={builderContext}
      registeredComponents={builderComponents}
      linkComponent={builderLinkComponent}
    />
  );

  const ConfirmationViewBlocks = (
    <Blocks
      blocks={confirmationView}
      parent={builderId}
      path="confirmationView"
      context={builderContext}
      registeredComponents={builderComponents}
      linkComponent={builderLinkComponent}
    />
  );

  // In editing/preview mode, show both slots for easier editing
  if (isEditing() || isPreviewing()) {
    return (
      <div
        {...attributes}
        data-itsm-view={currentView}
        className={`flex flex-col gap-4 ${attributes?.className ?? ""}`}
      >
        <div className="flex flex-col gap-2">
          <span className="border-b border-black font-bold">
            Table View (shown when itsmView = &quot;table&quot;):
          </span>
          <div className="relative">{TableViewBlocks}</div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="border-b border-black font-bold">
            Confirmation View (shown when itsmView = &quot;confirmation&quot;):
          </span>
          {ConfirmationViewBlocks}
        </div>
      </div>
    );
  }

  return (
    <div {...attributes} data-itsm-view={currentView}>
      {isTableView && <div className="relative">{TableViewBlocks}</div>}
      {isConfirmationView && ConfirmationViewBlocks}
    </div>
  );
};
